import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { REMOTE_PERMISSIONS } from '@backrs_ui/common-ui';

import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useIsInstitutionAdmin } from '@backrs_ui/grow-ui/src/hooks/useIsInstitutionAdmin';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import {
  useGetCoachingDashboardFiltersQuery,
  useGetInstitutionCoachesQuery,
  useGetMyAssigneesQuery,
  useGetMyInstitutionQuery,
  type CoachingDashboardFilter,
  type CoachingDashboardSort,
  type CoachingDashboardSortField,
  type CoachSummary,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { firstOperatorContextRefusal } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  VERIFIED_FILTER,
  type CoachingDashboardFilterAction,
  type CoachingDashboardFilterState,
} from './coachingDashboardFilters';
import { parseRosterTab, rolesForTab, ROSTER_TAB, type RosterTab } from './coachingDashboardTabs';
import {
  applyParamUpdates,
  DASHBOARD_PARAM as PARAM,
  parseCohortListParam,
  parseIntParam,
  parseListParam,
  parseNullableIntParam,
  parseNumberListParam,
  type ParamUpdates,
} from './dashboardUrlParams';

const DEFAULT_ROWS_PER_PAGE = 50;

const NO_COACHES: CoachSummary[] = [];

const SORT_FIELDS: readonly CoachingDashboardSortField[] = [
  'last_name',
  'grade',
  'last_login',
  'student_count',
];

const parseSortParam = (v: string | null): CoachingDashboardSort | undefined => {
  if (!v) return undefined;

  const [field, direction] = v.split(':');

  if (direction !== 'ASC' && direction !== 'DESC') return undefined;
  if (!SORT_FIELDS.includes(field as CoachingDashboardSortField)) return undefined;

  return { field: field as CoachingDashboardSortField, direction };
};

const serializeSort = (s: CoachingDashboardSort | undefined): string | null =>
  s ? `${s.field}:${s.direction}` : null;

export const useCoachingDashboardController = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (updates: ParamUpdates, options?: { replace?: boolean }) => {
      setSearchParams((prev) => applyParamUpdates(prev, updates), options);
    },
    [setSearchParams],
  );

  const page = parseIntParam(searchParams.get(PARAM.page), 0);
  const rowsPerPage = parseIntParam(searchParams.get(PARAM.rowsPerPage), DEFAULT_ROWS_PER_PAGE);
  const urlSearchValue = searchParams.get(PARAM.q) ?? '';
  const sort = useMemo(() => parseSortParam(searchParams.get(PARAM.sort)), [searchParams]);

  const filters = useMemo<CoachingDashboardFilterState>(
    () => ({
      grades: parseListParam(searchParams.get(PARAM.grades)),
      schools: parseListParam(searchParams.get(PARAM.schools)),
      roleIds: parseNumberListParam(searchParams.get(PARAM.roleIds)),
      programCodes: parseListParam(searchParams.get(PARAM.programs)),
      institutionRoles: parseListParam(searchParams.get(PARAM.institutionRoles)),
      verified: parseListParam(searchParams.get(PARAM.verified)),
      cohortIds: parseCohortListParam(searchParams.get(PARAM.cohortIds)),
      coachIds: parseNumberListParam(searchParams.get(PARAM.coachIds)),
    }),
    [searchParams],
  );

  const [searchValue, setSearchValue] = useState<string>(() => urlSearchValue);
  const debouncedSearchValue = useDebounce(searchValue, 300);
  const lastWrittenQRef = useRef<string>(urlSearchValue);

  useEffect(() => {
    const trimmed = debouncedSearchValue.trim();

    if (trimmed === lastWrittenQRef.current) return;

    lastWrittenQRef.current = trimmed;
    updateParams(
      { [PARAM.q]: trimmed === '' ? null : trimmed, [PARAM.page]: null },
      { replace: true },
    );
  }, [debouncedSearchValue, updateParams]);

  useEffect(() => {
    const urlQ = searchParams.get(PARAM.q) ?? '';

    if (urlQ === lastWrittenQRef.current) return;

    lastWrittenQRef.current = urlQ;
    setSearchValue(urlQ);
  }, [searchParams]);

  const activeTab = parseRosterTab(searchParams.get(PARAM.tab));

  const inScopeInstitutionId = parseNullableIntParam(searchParams.get(PARAM.institutionId));

  const onOperatorInstitutionSelect = useOperatorInstitutionSwitch();
  const isInstitutionAdmin = useIsInstitutionAdmin();
  const canAccessCoachAssignees = useHasPermission(REMOTE_PERMISSIONS.ACCESS_COACH_ASSIGNEES);

  const isCoach = !isInstitutionAdmin && canAccessCoachAssignees;
  const shouldFetchAssignees = !isInstitutionAdmin && canAccessCoachAssignees;
  const shouldFetchFilters = isInstitutionAdmin || shouldFetchAssignees;

  const {
    data: filterOptions,
    error: filtersError,
    isSuccess: hasFiltersAnswered,
  } = useGetCoachingDashboardFiltersQuery(undefined, { skip: !shouldFetchFilters });

  // An operator switch can only rewrite the history entry it is standing on, so going back
  // restores an institution belonging to a root the caller has left.
  const reachableInstitutions = filterOptions?.institutions;

  // If that endpoint is ever paginated, this stops being a reach check and starts silently
  // dropping legitimate narrowings.
  const isNarrowingUnreachable =
    inScopeInstitutionId !== null &&
    reachableInstitutions !== undefined &&
    !reachableInstitutions.some((institution) => institution.id === inScopeInstitutionId);

  // A read carrying an unchecked narrowing is refused, so it waits for the reach rather than
  // going out to be told no.
  const isNarrowingUnchecked =
    inScopeInstitutionId !== null &&
    !hasFiltersAnswered &&
    filtersError === undefined &&
    shouldFetchFilters;

  const appliedInstitutionId = isNarrowingUnreachable ? null : inScopeInstitutionId;

  useEffect(() => {
    if (!isNarrowingUnreachable) return;

    updateParams({ [PARAM.institutionId]: null, [PARAM.page]: null }, { replace: true });
  }, [isNarrowingUnreachable, updateParams]);

  const filterRoles = useMemo(
    () => rolesForTab(activeTab, filterOptions),
    [activeTab, filterOptions],
  );

  const isCoachesTab = isInstitutionAdmin && activeTab === ROSTER_TAB.COACHES;
  const isStaffTab = isInstitutionAdmin && activeTab === ROSTER_TAB.STAFF;
  const isPeopleTab = isCoachesTab || isStaffTab;
  // Every filter below is sent only when its own control is on screen, so a URL value
  // cannot narrow the roster with nothing saying so. The bar renders from these same flags.
  const showStudentFilters = !isPeopleTab;
  const showInstitutionFilters = !isCoach;
  const showRoleFilter = showStudentFilters && !showInstitutionFilters;
  const showProgramFilter = showStudentFilters && showInstitutionFilters;
  const showInstitutionRoleFilter = showInstitutionFilters && isCoachesTab;
  const showCohortFilter = showStudentFilters && showInstitutionFilters;
  const showCoachFilter = isInstitutionAdmin && showStudentFilters;

  const cohortOptions = useMemo(
    () =>
      (filterOptions?.cohorts ?? []).filter(
        (cohort) => appliedInstitutionId === null || cohort.institution_id === appliedInstitutionId,
      ),
    [appliedInstitutionId, filterOptions?.cohorts],
  );

  const offeredCohortIds = useMemo(
    () =>
      filters.cohortIds.filter(
        (id): id is number =>
          typeof id === 'number' && cohortOptions.some((cohort) => cohort.id === id),
      ),
    [cohortOptions, filters.cohortIds],
  );

  const isCohortPickUnchecked =
    showCohortFilter &&
    filters.cohortIds.some((id) => typeof id === 'number') &&
    !hasFiltersAnswered &&
    filtersError === undefined;

  const {
    data: coachOptions = NO_COACHES,
    currentData: currentCoachOptions,
    isError: isCoachesError,
  } = useGetInstitutionCoachesQuery(
    appliedInstitutionId === null ? {} : { institutionIds: [appliedInstitutionId] },
    { skip: !showCoachFilter || isNarrowingUnchecked },
  );

  // A picked coach the field cannot show would narrow the roster under an "All" label.
  const offeredCoachIds = useMemo(
    () =>
      filters.coachIds.filter((id) =>
        (currentCoachOptions ?? NO_COACHES).some((coach) => coach.id === id),
      ),
    [currentCoachOptions, filters.coachIds],
  );

  // Held like an unchecked narrowing, so a linked coach pick never shows the full roster first.
  // currentData, not data: data still holds the previous institution's coaches after a change.
  const isCoachPickUnchecked =
    showCoachFilter &&
    filters.coachIds.length > 0 &&
    currentCoachOptions === undefined &&
    !isCoachesError;

  const start = page * rowsPerPage;
  const end = start + rowsPerPage - 1;

  const filter = useMemo<CoachingDashboardFilter | undefined>(() => {
    const trimmed = debouncedSearchValue.trim();
    const next: CoachingDashboardFilter = {
      ...(isInstitutionAdmin ? { role_group: activeTab } : null),
      ...(trimmed ? { q: trimmed } : null),
      ...(showStudentFilters && filters.grades.length > 0 ? { grades: filters.grades } : null),
      ...(showStudentFilters && filters.schools.length > 0 ? { schools: filters.schools } : null),
      ...(showRoleFilter && filters.roleIds.length > 0 ? { role_ids: filters.roleIds } : null),
      ...(showProgramFilter && filters.programCodes.length > 0
        ? { offering_codes: filters.programCodes }
        : null),
      ...(appliedInstitutionId !== null ? { institution_ids: [appliedInstitutionId] } : null),
      ...(showInstitutionRoleFilter && filters.institutionRoles.length > 0
        ? { institution_roles: filters.institutionRoles }
        : null),
      ...(showStudentFilters && filters.verified.length === 1
        ? { is_verified: filters.verified[0] === VERIFIED_FILTER.VERIFIED }
        : null),
      ...(showCohortFilter && offeredCohortIds.length > 0 ? { cohort_id: offeredCohortIds } : null),
      ...(showCohortFilter && filters.cohortIds.includes('none') ? { without_cohort: true } : null),
      ...(showCoachFilter && offeredCoachIds.length > 0 ? { coach_ids: offeredCoachIds } : null),
    };

    return Object.keys(next).length > 0 ? next : undefined;
  }, [
    activeTab,
    appliedInstitutionId,
    debouncedSearchValue,
    filters,
    isInstitutionAdmin,
    offeredCoachIds,
    offeredCohortIds,
    showCoachFilter,
    showCohortFilter,
    showInstitutionRoleFilter,
    showProgramFilter,
    showRoleFilter,
    showStudentFilters,
  ]);

  const queryParams = {
    range: [start, end] as [number, number],
    filter,
    sort,
  };

  const onToggleSort = useCallback(
    (field: CoachingDashboardSortField) => {
      const current = parseSortParam(searchParams.get(PARAM.sort));
      let nextSort: CoachingDashboardSort | undefined;

      if (current?.field !== field) {
        nextSort = { field, direction: 'ASC' };
      } else if (current.direction === 'ASC') {
        nextSort = { field, direction: 'DESC' };
      } else {
        nextSort = undefined;
      }

      updateParams({ [PARAM.sort]: serializeSort(nextSort), [PARAM.page]: null });
    },
    [searchParams, updateParams],
  );

  const {
    data: institutionData,
    isFetching: isFetchingInstitution,
    isError: isInstitutionError,
    error: institutionError,
  } = useGetMyInstitutionQuery(queryParams, {
    skip:
      !isInstitutionAdmin || isNarrowingUnchecked || isCoachPickUnchecked || isCohortPickUnchecked,
  });

  const {
    data: assigneesData,
    isFetching: isFetchingAssignees,
    isError: isAssigneesError,
    error: assigneesError,
  } = useGetMyAssigneesQuery(queryParams, {
    skip: !shouldFetchAssignees || isNarrowingUnchecked,
  });

  const onSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const onTabChange = useCallback(
    (tab: RosterTab) => {
      updateParams({
        [PARAM.tab]: tab === ROSTER_TAB.STUDENTS ? null : tab,
        [PARAM.page]: null,
        [PARAM.sort]: null,
        // Each tab shows its own filters, so a value carried across would narrow the
        // destination roster with no control offering a way back.
        [PARAM.grades]: null,
        [PARAM.schools]: null,
        [PARAM.roleIds]: null,
        [PARAM.programs]: null,
        [PARAM.institutionRoles]: null,
        [PARAM.verified]: null,
        [PARAM.cohortIds]: null,
        [PARAM.coachIds]: null,
      });
    },
    [updateParams],
  );

  const onPageChange = useCallback(
    (_: unknown, newPage: number) => {
      updateParams({ [PARAM.page]: newPage === 0 ? null : newPage });
    },
    [updateParams],
  );

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const n = parseInt(event.target.value, 10);

      updateParams({
        [PARAM.rowsPerPage]: n === DEFAULT_ROWS_PER_PAGE ? null : n,
        [PARAM.page]: null,
      });
    },
    [updateParams],
  );

  const dispatchFilters = useCallback(
    (action: CoachingDashboardFilterAction) => {
      const updates: ParamUpdates = { [PARAM.page]: null };

      switch (action.type) {
        case 'setGrades':
          updates[PARAM.grades] = action.values;
          break;
        case 'setSchools':
          updates[PARAM.schools] = action.values;
          break;
        case 'setRoleIds':
          updates[PARAM.roleIds] = action.values.map(String);
          break;
        case 'setProgramCodes':
          updates[PARAM.programs] = action.values;
          break;
        case 'setInstitutionRoles':
          updates[PARAM.institutionRoles] = action.values;
          break;
        case 'setVerified':
          updates[PARAM.verified] = action.values;
          break;
        case 'setCohortIds':
          updates[PARAM.cohortIds] = action.values.map(String);
          break;
        case 'setCoachIds':
          updates[PARAM.coachIds] = action.values.map(String);
          break;
        case 'clear':
          updates[PARAM.grades] = null;
          updates[PARAM.schools] = null;
          updates[PARAM.roleIds] = null;
          updates[PARAM.programs] = null;
          updates[PARAM.institutionRoles] = null;
          updates[PARAM.verified] = null;
          updates[PARAM.cohortIds] = null;
          updates[PARAM.coachIds] = null;
          updates[PARAM.institutionId] = null;
          updates[PARAM.q] = null;
          lastWrittenQRef.current = '';
          setSearchValue('');
          break;
      }
      updateParams(updates);
    },
    [updateParams],
  );

  const responseData = assigneesData ?? institutionData;
  const members = responseData?.data ?? [];
  const totalCount = responseData?.total ?? 0;
  const isLoading = isFetchingAssignees || isFetchingInstitution;
  const isRosterUnavailable = isInstitutionError || isAssigneesError;

  const onInScopeInstitutionChange = useCallback(
    (institutionId: number | null) => {
      updateParams({
        [PARAM.institutionId]: institutionId,
        [PARAM.cohortIds]: null,
        [PARAM.coachIds]: null,
        [PARAM.page]: null,
      });
    },
    [updateParams],
  );

  const peopleErrors = useMemo(
    () => [institutionError, assigneesError, filtersError],
    [assigneesError, filtersError, institutionError],
  );

  const inScopeInstitutions = filterOptions?.institutions ?? [];

  const heldRoots = useMemo(() => firstOperatorContextRefusal(peopleErrors), [peopleErrors]);

  return {
    members,
    totalCount,
    peopleErrors,
    heldRoots,
    onOperatorInstitutionSelect,
    inScopeInstitutionId: appliedInstitutionId,
    onInScopeInstitutionChange,
    isLoading,
    isRosterUnavailable,
    isCoach,
    isInstitutionAdmin,
    isPeopleTab,
    showStudentFilters,
    showInstitutionFilters,
    activeTab,
    onTabChange,
    page,
    rowsPerPage,
    searchValue,
    filters,
    dispatchFilters,
    filterOptions,
    cohortOptions,
    showCohortFilter,
    coachOptions,
    showCoachFilter,
    showInstitutionRoleFilter,
    inScopeInstitutions,
    filtersError,
    filterRoles,
    sort,
    onSearchChange,
    onPageChange,
    onRowsPerPageChange,
    onToggleSort,
  };
};
